import * as React from "react";
import { toast } from "sonner";
import { Toast } from "@/kit/ui/toast";

/// Milliseconds
export const DEFAULT_TOAST_DURATION = 4_000;

export type ToastLevel = "info" | "success" | "warning" | "error";

export interface ToastOptions {
  id?: string | number;
  title?: React.ReactNode;
  message: React.ReactNode;
  level: ToastLevel;
  dismissible?: boolean;
  duration?: number;
  button?: {
    children: React.ReactNode;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
  };
}

export interface ToastProps {
  id: string | number;
  title?: React.ReactNode;
  message: React.ReactNode;
  level: ToastLevel;
  dismissible: boolean;
  duration: number;
  button?: {
    children: React.ReactNode;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
  };
}

const createToast = ({
  id,
  dismissible = true,
  duration = DEFAULT_TOAST_DURATION,
  ...props
}: ToastOptions) => {
  return toast.custom(
    (id) => {
      return Toast({ id, dismissible, duration, ...props });
    },
    { id, dismissible, duration },
  );
};

const basicToast = (
  message: string | React.ReactNode,
  options?: Omit<ToastOptions, "message">,
) => {
  const level = options?.level || "info";
  createToast({ message, level, ...options });
};

const infoToast = (
  message: string | React.ReactNode,
  options?: Omit<ToastOptions, "level" | "message">,
) => {
  createToast({ message, level: "info", ...options });
};

const successToast = (
  message: string | React.ReactNode,
  options?: Omit<ToastOptions, "level" | "message">,
) => {
  createToast({ message, level: "success", ...options });
};

const warningToast = (
  message: string | React.ReactNode,
  options?: Omit<ToastOptions, "level" | "message">,
) => {
  createToast({ message, level: "warning", ...options });
};

const errorToast = (
  message: string | React.ReactNode,
  options?: Omit<ToastOptions, "level" | "message">,
) => {
  createToast({ message, level: "error", ...options });
};

const effectToast = (
  message: string | React.ReactNode,
  { level, ...options }: Omit<ToastOptions, "message" | "duration"> = {
    level: "info",
    dismissible: false,
  },
) => {
  const id = createToast({
    message,
    level,
    duration: Infinity,
    ...options,
  });
  return () => {
    toast.dismiss(id);
  };
};

const toastUtil = Object.assign(basicToast, {
  dismiss: toast.dismiss,
  create: basicToast,
  info: infoToast,
  success: successToast,
  warning: warningToast,
  error: errorToast,
  promise: toast.promise,
  effect: effectToast,
});

export { toastUtil as toast };
